/**
 * EventCard — the rich calendar event card used by the compact lists and the
 * all-day strip. Carries time range, title, location, owner avatars, a
 * status-colored left-border accent + label, and an optional unread dot.
 *
 * The full timeline uses the height-aware `TimelineEvent` instead, because a
 * fixed-height absolutely-positioned card must adapt which fields it shows.
 */

import { MapPin } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { CSSProperties } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { OwnerAvatars } from './OwnerAvatars';
import { EventDetail } from './EventDetail';
import type { NormalizedEvent } from './calendar.types';
import { formatTimeRange, statusAccent, htmlToPlainText } from './calendar.helpers';
import { cleanDisplayText } from '@/utils/display-text';

export interface EventCardProps {
  event: NormalizedEvent;
  /** Tighter padding + smaller type for dense week columns. */
  compact?: boolean;
  hour12?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function EventCard({ event, compact = false, hour12, className, style }: EventCardProps) {
  const accent = statusAccent(event.status);
  const overrideStyle: CSSProperties | undefined = event.color
    ? { ...style, borderLeftColor: event.color }
    : style;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="calendar-event-card"
          style={overrideStyle}
          className={cn(
            'group flex w-full flex-col overflow-hidden rounded-lg border border-border border-l-2 bg-card text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-muted-foreground/50 focus-visible:ring-offset-[3px] focus-visible:ring-offset-background',
            accent?.border,
            compact ? 'gap-0.5 p-2' : 'gap-1 p-3',
            className,
          )}
        >
          {/* Top row: time + status */}
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            'truncate text-muted-foreground tabular-nums',
            compact ? 'text-[11px]' : 'text-xs',
          )}
        >
          {formatTimeRange(event, hour12)}
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          {event.unread && (
            <span aria-label="Unread" className="size-1.5 rounded-full bg-status-info-fg" />
          )}
          {accent && (
            <span className="flex items-center gap-1">
              <span className={cn('size-1.5 rounded-full', accent.dot)} aria-hidden />
              {!compact && (
                <span className={cn('text-xs font-medium', accent.text)}>{accent.label}</span>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Title */}
      <h4 className={cn('truncate font-medium text-foreground', compact ? 'text-xs' : 'text-sm')}>
        {cleanDisplayText(event.title)}
      </h4>

      {/* Location */}
      {event.location && (
        <div
          className={cn(
            'flex min-w-0 items-center gap-1 text-muted-foreground',
            compact ? 'text-[11px]' : 'text-xs',
          )}
        >
          <MapPin className="size-3 shrink-0" />
          <span className="truncate">{event.location}</span>
        </div>
      )}

      {/* Description (full cards only) — flattened to plain text so HTML
          descriptions don't show raw tags in the two-line preview. The full
          rich description renders in the detail popover. */}
      {!compact && event.description && (
        <p className="line-clamp-2 text-xs text-muted-foreground">
          {htmlToPlainText(event.description)}
        </p>
      )}

          {/* Owners */}
          {event.owners && event.owners.length > 0 && (
            <div className={cn(compact ? 'mt-0.5' : 'mt-1')}>
              <OwnerAvatars owners={event.owners} size={compact ? 18 : 22} showName={!compact} />
            </div>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        collisionPadding={16}
        // Cap to the space Radix actually has on the chosen side (not a flat
        // 80vh, which can still overflow when the popover opens partway down
        // the viewport) and scroll the overflow.
        className="w-[26rem] max-w-[calc(100vw-2rem)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-0"
      >
        <EventDetail event={event} hour12={hour12} />
      </PopoverContent>
    </Popover>
  );
}
